"use client"

import Image from "next/image"
import { usePathname } from "next/navigation"
import { Section } from "@/components/layout/Section"
import { Container } from "@/components/layout/Container"
import { Palette, Shield, Type, Award, Eye, Sparkles } from "lucide-react"
import { CTA } from "@/components/sections/CTA"
import { BusinessCard } from "@/components/sections/BusinessCard"
import { type Locale } from "@/lib/i18n"

const brandValues = [
  {
    icon: Shield,
    title: "Güvenilirlik",
    description: "İş ortaklarımızla kurduğumuz güçlü ilişkiler ve sözümüzde durma prensibimiz.",
  },
  {
    icon: Sparkles,
    title: "Yenilikçilik",
    description: "Sürekli gelişim ve yenilikçi çözümlerle sektörde öncü olmak.",
  },
  {
    icon: Award,
    title: "Kalite",
    description: "Her projede en yüksek standartları hedefleyen kalite odaklı yaklaşımımız.",
  },
  {
    icon: Eye,
    title: "Şeffaflık",
    description: "Açık iletişim ve şeffaf süreçlerle müşteri memnuniyeti sağlamak.",
  },
]

export default function CorporateIdentityPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      {/* Hero Section */}
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6 font-mono">
              Kurumsal Kimlik
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed">
              Markamızın görsel kimliği ve kurumsal standartlarımız. LAKVERSE olarak, tutarlı ve profesyonel bir marka deneyimi sunuyoruz.
            </p>
          </div>
        </Container>
      </Section>

      {/* Logo Section */}
      <Section className="py-16 lg:py-24 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-mono">
                Logo Kullanımı
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                Logo'muz, markamızın en önemli görsel öğesidir. Doğru kullanımı marka bütünlüğümüz için kritiktir.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {/* Koyu Arka Plan */}
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 font-mono">
                  Koyu Arka Plan
                </h3>
                <div className="bg-slate-900 p-8 sm:p-12 rounded-lg flex items-center justify-center min-h-[200px] sm:min-h-[250px] border-2 border-slate-800">
                  <Image
                    src="/LAKVERSE-white.png"
                    alt="LAKVERSE"
                    width={384}
                    height={128}
                    className="h-16 sm:h-20 md:h-24 w-auto"
                    quality={70}
                    sizes="(max-width: 640px) 256px, (max-width: 768px) 320px, 384px"
                  />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Beyaz logo, koyu arka planlar üzerinde kullanılır. Minimum boşluk alanı logo yüksekliğinin yarısı kadar olmalıdır.
                </p>
              </div>

              {/* Açık Arka Plan */}
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 font-mono">
                  Açık Arka Plan
                </h3>
                <div className="bg-white border-2 border-slate-200 p-8 sm:p-12 rounded-lg flex items-center justify-center min-h-[200px] sm:min-h-[250px]">
                  <Image
                    src="/LAKVERSE-blue.png"
                    alt="LAKVERSE"
                    width={384}
                    height={128}
                    className="h-16 sm:h-20 md:h-24 w-auto"
                    quality={70}
                    sizes="(max-width: 640px) 256px, (max-width: 768px) 320px, 384px"
                  />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Renkli logo, açık arka planlar üzerinde kullanılır. Logo'nun etrafında yeterli boşluk bırakılmalıdır.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Color Palette Section */}
      <Section className="py-16 lg:py-24 bg-slate-50">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-mono">
                Renk Paleti
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                Markamızın renk paleti, tutarlı ve profesyonel bir görsel kimlik oluşturmak için tasarlanmıştır.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {/* Ana Renk */}
              <div className="bg-white p-6 sm:p-8 rounded-lg border border-slate-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-6 font-mono">
                  Ana Renk
                </h3>
                <div className="space-y-6">
                  <div className="w-full h-32 sm:h-40 bg-[rgb(3,74,166)] rounded-lg border-2 border-slate-200 shadow-md"></div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="font-semibold text-slate-700">HEX:</span>
                      <span className="font-mono text-slate-900">#034AA6</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="font-semibold text-slate-700">RGB:</span>
                      <span className="font-mono text-slate-900">3, 74, 166</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="font-semibold text-slate-700">CMYK:</span>
                      <span className="font-mono text-slate-900">100, 70, 0, 20</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Ana marka rengi, kurumsal kimliğimizin temelini oluşturur. Logo, başlıklar ve vurgu öğelerinde kullanılır.
                  </p>
                </div>
              </div>

              {/* Vurgu Rengi */}
              <div className="bg-white p-6 sm:p-8 rounded-lg border border-slate-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-6 font-mono">
                  Vurgu Rengi
                </h3>
                <div className="space-y-6">
                  <div className="w-full h-32 sm:h-40 bg-[rgb(2,40,89)] rounded-lg border-2 border-slate-200 shadow-md"></div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="font-semibold text-slate-700">HEX:</span>
                      <span className="font-mono text-slate-900">#022859</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="font-semibold text-slate-700">RGB:</span>
                      <span className="font-mono text-slate-900">2, 40, 89</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="font-semibold text-slate-700">CMYK:</span>
                      <span className="font-mono text-slate-900">100, 85, 0, 50</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Vurgu rengi, ikincil öğeler, hover efektleri ve gradyan arka planlarda kullanılır.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Typography Section */}
      <Section className="py-16 lg:py-24 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-mono">
                Tipografi
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                Markamızın tipografik kimliği, okunabilirlik ve modern görünümü bir araya getirir.
              </p>
            </div>

            <div className="bg-slate-50 p-6 sm:p-8 lg:p-12 rounded-lg border border-slate-200">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">
                    Ana Font: JetBrains Mono
                  </h3>
                  <div className="bg-white p-6 sm:p-8 rounded-lg border border-slate-200">
                    <div className="mb-4">
                      <Image
                        src="/LAKVERSE-black.png"
                        alt="LAKVERSE"
                        width={320}
                        height={107}
                        className="h-12 sm:h-16 md:h-20 w-auto"
                        quality={70}
                        sizes="(max-width: 640px) 192px, (max-width: 768px) 256px, 320px"
                      />
                    </div>
                    <div className="text-xl sm:text-2xl font-semibold mb-4 font-mono text-slate-700">
                      Modern Yazılım Çözümleri
                    </div>
                    <div className="text-base sm:text-lg font-normal mb-4 font-mono text-slate-600">
                      Profesyonel web tasarım, mobil uygulama geliştirme ve e-ticaret çözümleri.
                    </div>
                    <div className="text-sm font-light font-mono text-slate-500">
                      JetBrains Mono, modern ve teknik bir görünüm sunar. Kod ve metin için ideal bir font ailesidir.
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                  <div className="bg-white p-4 sm:p-6 rounded-lg border border-slate-200">
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Light (300)</div>
                    <div className="text-lg font-light font-mono text-slate-900">İnce vurgular</div>
                  </div>
                  <div className="bg-white p-4 sm:p-6 rounded-lg border border-slate-200">
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Regular (400)</div>
                    <div className="text-lg font-normal font-mono text-slate-900">Varsayılan metin</div>
                  </div>
                  <div className="bg-white p-4 sm:p-6 rounded-lg border border-slate-200">
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Bold (700)</div>
                    <div className="text-lg font-bold font-mono text-slate-900">Başlıklar</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Brand Values Section */}
      <Section className="py-16 lg:py-24 bg-slate-50">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-mono">
                Marka Değerleri
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                LAKVERSE olarak, bu değerler markamızın temelini oluşturur ve tüm çalışmalarımızda rehberimizdir.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {brandValues.map((value, index) => {
                const Icon = value.icon
                return (
                  <div
                    key={index}
                    className="bg-white p-6 sm:p-8 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="mb-4 inline-flex p-3 bg-[rgb(3,74,166)] text-white rounded-lg">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 font-mono">
                      {value.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </Container>
      </Section>

      <BusinessCard locale={currentLocale} />

      <CTA locale={currentLocale} />
    </>
  )
}
